import React, { useCallback, useEffect, useState } from 'react';
import ComoFunciona, { ComoFuncionaSecao } from '../../../components/como-funciona/ComoFunciona';
import { fetchSettingsPortal, atualizarSettingPortal } from '../../../api/portalCotacoes/portalCotacoesApi';
import { notify } from '../../../shared/feedback/notify';
import '../../../components/como-funciona/help-circle-btn.css';
import '../../../shared/feedback/erro-de-tela.css';

/**
 * Seção "Tempo por etapa" da configuração de Cotações (at-11, 28/09/2026 — pedido do Victor).
 *
 * O que ela liga: o aviso diário de **etapa parada**. Até a at-11 existia um alerta de tempo
 * só, e de uma etapa só (a véspera do prazo de resposta) — cotação parada em decisão,
 * esperando o gestor ou pronta para gerar pedidos ficava parada em silêncio.
 *
 * ⚠️ As etapas de RESPOSTA não aparecem aqui de propósito: elas já têm relógio próprio, o
 * prazo que o comprador define ao criar a cotação. Dois relógios na mesma etapa dariam avisos
 * que se contradizem.
 *
 * Estilo: nada de CSS próprio — card, hint, input e botão vêm de `adminSistema.css`, como as
 * outras abas (molde do `FormasPagamentoSection`).
 */

const CHAVE = 'ALERTAS_ETAPA_DIAS';

/**
 * As etapas configuráveis, na ordem do fluxo, com o rótulo que a tela mostra.
 *
 * ⚠️ Espelha `ETAPAS_COM_LIMIAR` do backend, que é quem recusa chave desconhecida no
 * `PUT /settings/:key`. Etapa nova entra nos dois lados.
 */
const ETAPAS: { chave: string; rotulo: string; explicacao: string }[] = [
  {
    chave: 'RASCUNHO',
    rotulo: 'Rascunho',
    explicacao: 'Criada e ainda não enviada aos fornecedores.',
  },
  {
    chave: 'EM_DECISAO',
    rotulo: 'Em decisão',
    explicacao: 'Respostas encerradas, esperando o comprador escolher e fechar a cesta.',
  },
  {
    chave: 'AGUARDANDO_APROVACAO',
    rotulo: 'Aguardando o gestor',
    explicacao: 'Cesta fechada com item acima do menor preço. Aqui o gestor também é avisado.',
  },
  {
    chave: 'GERANDO_PEDIDOS',
    rotulo: 'Pronta para gerar os pedidos',
    explicacao: 'Cesta aprovada, esperando a geração dos pedidos no ERP.',
  },
];

const helpSections: ComoFuncionaSecao[] = [
  {
    titulo: 'O que estes prazos fazem',
    conteudo: (
      <p>
        Uma vez por dia o sistema olha em que etapa cada cotação está e <strong>há quanto tempo</strong>. Passou
        do número configurado aqui, quem conduz a cotação recebe um aviso no sino — com o tempo parado e o que se
        espera que aconteça para ela andar. Na etapa de aprovação, <strong>os gestores também são avisados</strong>:
        ali a ação não é do comprador.
      </p>
    ),
  },
  {
    titulo: 'Etapa em branco não é vigiada',
    conteudo: (
      <p>
        Deixar o campo <strong>vazio</strong> desliga o aviso daquela etapa — nenhuma cotação nela vira aviso, por
        mais tempo que passe. É assim que se para de vigiar uma etapa sem precisar de código. O menor valor
        aceito é <strong>1 dia</strong>: zero faria toda cotação nascer atrasada.
      </p>
    ),
  },
  {
    titulo: 'As etapas de resposta não estão aqui',
    conteudo: (
      <p>
        Enquanto a cotação espera propostas, quem manda é o <strong>prazo de resposta</strong> que o comprador
        definiu ao criá-la: o sistema avisa na véspera, dizendo quantos fornecedores ainda não responderam, e de
        novo quando o prazo vence. Um segundo relógio sobre a mesma etapa produziria avisos contraditórios.
      </p>
    ),
  },
  {
    titulo: 'De onde vêm os números de fábrica',
    conteudo: (
      <p>
        Foram <strong>escolhidos, não medidos</strong>: o Portal ainda não tem histórico de uso que permita dizer
        quanto cada etapa costuma levar. Ajuste conforme a operação real — e, se um aviso começar a aparecer todo
        dia sem nada de errado, o número está baixo demais: alerta que sempre aparece é alerta que se aprende a
        ignorar.
      </p>
    ),
  },
];

interface AlertasEtapaSectionProps {
  /**
   * Só ADMIN/TI gravam configuração no Portal (`PUT /settings/:key`). Oferecer os controles a
   * quem não pode gravar daria um caminho que só devolve 403 (ADR 0012).
   */
  podeEditar: boolean;
}

const AlertasEtapaSection: React.FC<AlertasEtapaSectionProps> = ({ podeEditar }) => {
  /** Valores como TEXTO: o campo pode estar vazio, que é "não vigiar". */
  const [valores, setValores] = useState<Record<string, string>>({});
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [ajudaAberta, setAjudaAberta] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const settings = await fetchSettingsPortal();
      const bruto = (settings as Record<string, string>)[CHAVE];
      const limiares = bruto ? JSON.parse(bruto) : {};
      setValores(
        Object.fromEntries(
          ETAPAS.map((e) => [e.chave, limiares?.[e.chave] != null ? String(limiares[e.chave]) : '']),
        ),
      );
      setErro(false);
    } catch (error) {
      /*
       * Campos vazios por erro seriam lidos como "nenhuma etapa vigiada" — afirmação de
       * negócio tirada de falha de API (regra da at-60), e das piores: o ADMIN concluiria
       * que precisa configurar tudo de novo, e salvar por cima **desligaria** os avisos que
       * estão no ar.
       */
      setValores({});
      setErro(true);
      notify.errorFrom(
        error,
        'Não foi possível carregar os prazos por etapa. Recarregue a página; se persistir, acione a TI.',
      );
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  async function salvar() {
    const limiares: Record<string, number> = {};
    for (const etapa of ETAPAS) {
      const texto = (valores[etapa.chave] ?? '').trim();
      if (texto === '') continue; // vazio = não vigiar
      const dias = Number(texto);
      if (!Number.isInteger(dias) || dias < 1) {
        notify.warning(
          `"${etapa.rotulo}": informe um número inteiro de dias a partir de 1, ou deixe vazio para não vigiar.`,
        );
        return;
      }
      limiares[etapa.chave] = dias;
    }

    setSalvando(true);
    try {
      await atualizarSettingPortal(CHAVE, JSON.stringify(limiares));
      notify.success('Prazos por etapa salvos com sucesso.');
    } catch (error) {
      // Nunca dar o salvamento como feito: a tela mantém o que o usuário digitou, para ele
      // poder tentar de novo sem redigitar.
      notify.errorFrom(error, 'Não foi possível salvar os prazos por etapa.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <>
      <div className="admin-card">
        <div className="admin-card-title">
          Tempo esperado em cada etapa
          <button
            type="button"
            className="help-circle-btn"
            title="Como Funciona"
            onClick={() => setAjudaAberta(true)}
          >
            ?
          </button>
        </div>

        <p className="admin-hint" style={{ marginTop: 0 }}>
          {podeEditar
            ? 'Passado o número de dias, quem conduz a cotação recebe um aviso no sino, uma vez por dia. Campo vazio não vigia a etapa.'
            : 'Passado o número de dias, quem conduz a cotação recebe um aviso no sino. Os prazos são mantidos por um administrador ou pela TI.'}
        </p>

        {erro ? (
          <p className="erro-de-tela">
            Não foi possível carregar os prazos, então não dá para saber quais avisos estão valendo hoje.
            Recarregue a página antes de editar — salvar agora poderia desligar avisos que estão no ar.
          </p>
        ) : carregando ? (
          <p className="admin-hint">Carregando...</p>
        ) : (
          <>
            {ETAPAS.map((etapa) => (
              <div key={etapa.chave} className="admin-input-group" style={{ marginBottom: 12 }}>
                <label className="admin-label" htmlFor={`etapa-${etapa.chave}`}>
                  {etapa.rotulo}
                </label>
                <input
                  id={`etapa-${etapa.chave}`}
                  className="admin-input"
                  type="number"
                  min={1}
                  step={1}
                  placeholder="não vigiar"
                  disabled={!podeEditar || salvando}
                  value={valores[etapa.chave] ?? ''}
                  onChange={(e) =>
                    setValores((atual) => ({ ...atual, [etapa.chave]: e.target.value }))
                  }
                />
                <p className="admin-hint" style={{ margin: '4px 0 0' }}>
                  {etapa.explicacao}
                </p>
              </div>
            ))}

            {podeEditar && (
              <button type="button" className="admin-btn" onClick={salvar} disabled={salvando}>
                {salvando ? 'Salvando...' : 'Salvar prazos'}
              </button>
            )}
          </>
        )}
      </div>

      <ComoFunciona
        isOpen={ajudaAberta}
        onClose={() => setAjudaAberta(false)}
        titulo="Como Funciona - Tempo por etapa"
        secoes={helpSections}
      />
    </>
  );
};

export default AlertasEtapaSection;
